import React, { useState } from 'react';

import { RightOutlined } from '@ant-design/icons';
import { Checkbox, Drawer as DrawerAntD, Input, Select, Table } from 'antd';
import { T, TF } from 'react-translator-component';
import styled from 'styled-components';

import {
  ConnectionFilter,
  EMPTY_CONNECTION_FILTER,
  filterConnections,
} from './connectionFilters';


import { Connection } from '../../../../types/responses/Interoperability';
import StatusBadge from '../../../atoms/StatusBadge';
import { ConnectionDrawerDetails } from '../../../molecules/InteroperabilityDrawers';
import { Pagination } from '../../../molecules/Pagination/Pagination';
import { PartnerLogo } from '../../../molecules/PartnerLogo';
import {
  directionLabel,
  receivingLabel,
  sharingLabel,
  STATUS_STYLE,
} from '../contractRules';
import { clampPage, countLabel, pageOf } from '../listPaging';
import { Chevron, FilterBar, FilterCount, FilterSpacer } from '../styled';

/**
 * Aba Conexoes (MV2-1713).
 *
 * Tabela no mesmo padrao do resto do dashboard — antd Table mais painel lateral
 * pelo chevron, como o financeiro — em vez da grade de cartoes que estava aqui: as
 * sete colunas apertadas nao davam conta do escopo por conta, que e uma lista, e as
 * acoes de contrato ficavam a um clique de distancia numa linha de lista.
 *
 * O painel aberto vem da URL (?partner=), entao o link leva direto ao detalhe de um
 * parceiro.
 */

// O antd 4.10 nao tipa bem o Drawer com styled-components; o financeiro faz o
// mesmo alias.
const Drawer: any = DrawerAntD;

/* Checkbox no tema do dashboard: o antd deixa o marcado no azul padrão, fora do tema. */
const ThemedCheckbox = styled(Checkbox)`
  .ant-checkbox-checked .ant-checkbox-inner,
  &:hover .ant-checkbox-inner,
  .ant-checkbox:hover .ant-checkbox-inner {
    border-color: ${({ theme }) => theme.primary};
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: ${({ theme }) => theme.primary};
  }

  .ant-checkbox-checked::after {
    border-color: ${({ theme }) => theme.primary};
  }
`;

const Wrapper = styled.div`
  .ant-table-cell {
    font-size: 12px;
  }

  .interop-row {
    cursor: pointer;
  }
`;

const Note = styled.p`
  background: #fff;
  border: 1px solid #e3e6ea;
  border-radius: 8px;
  padding: 10px 14px;
  color: #4a5462;
  font-size: 11.5px;
  margin: 0 0 12px;
`;

const Partner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Name = styled.div`
  color: #12263f;
  font-weight: 600;
`;

const Sub = styled.div`
  color: #8a9099;
  font-size: 11px;
`;

const Chip = styled.span<{ bidirectional: boolean }>`
  background: ${({ bidirectional }) => (bidirectional ? '#eef4fe' : '#eef0f2')};
  color: ${({ bidirectional }) => (bidirectional ? '#1c4fae' : '#6b7280')};
  border-radius: 999px;
  padding: 2px 9px;
  font-size: 11px;
  white-space: nowrap;
`;

const Numeric = styled.div`
  font-variant-numeric: tabular-nums;
`;

const DrawerTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Empty = styled.div`
  background: #fff;
  border: 1px dashed #d9dce1;
  border-radius: 8px;
  padding: 32px;
  text-align: center;
  color: #8a9099;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.primary};
  cursor: pointer;
  text-decoration: underline;
  padding: 0 8px;
  font-size: 12px;
`;

/** Mesmo tamanho de página das outras listagens do módulo. */
const PAGE_SIZE = 25;

export type ConnectionsTabProps = {
  tenantId: string;
  connections: Connection[];
  onChanged: () => void;
  onInvite: () => void;
  onEnterToken: () => void;

  /** Parceiro com o painel aberto, vindo da URL. */
  expandedPartnerId: string | null;
  onExpandPartner: (partnerTenantId: string | null) => void;

  /**
   * Alternador de visão, desenhado dentro da barra de filtro.
   *
   * Entra como slot, e não como estado daqui: quem escolhe a visão é quem monta a aba, e
   * esta tela não precisa conhecer o mapa para ceder um lugar a ele na barra.
   */
  viewSwitch?: React.ReactNode;

  /**
   * O que aparece no lugar da tabela, recebendo os contratos JÁ FILTRADOS.
   *
   * Recebe o filtrado, e não a página: o mapa não pagina. E recebe filtrado, e não tudo,
   * porque com a barra de filtro visível nas duas visões o filtro tem de valer para as duas
   * — filtro que a tela mostra e não aplica é pior do que filtro nenhum.
   */
  mapView?: (filtered: Connection[]) => React.ReactNode;
};

export const ConnectionsTab: React.FC<ConnectionsTabProps> = ({
  tenantId,
  connections,
  onChanged,
  onInvite,
  onEnterToken,
  expandedPartnerId,
  onExpandPartner,
  viewSwitch,
  mapView,
}) => {
  const [filter, setFilter] = useState<ConnectionFilter>(EMPTY_CONNECTION_FILTER);
  const [page, setPage] = useState(1);

  const opened = connections.find(
    (connection) => connection.partner_tenant_id === expandedPartnerId,
  );

  /*
    Filtro e paginação no cliente: o endpoint devolve todos os contratos da conta, já
    agrupados por hierarquia do parceiro. Com o conjunto completo em mãos o resultado é o
    mesmo que faria no servidor — e agrupar lá para filtrar aqui seria refazer o agrupamento.
  */
  const filtered = filterConnections(connections, filter);
  const currentPage = clampPage(page, filtered.length, PAGE_SIZE);
  const visible = pageOf(filtered, currentPage, PAGE_SIZE);

  /** Trocar filtro volta para a primeira página: a atual pode não existir no resultado. */
  const changeFilter = (change: Partial<ConnectionFilter>) => {
    setFilter((current) => ({ ...current, ...change }));
    setPage(1);
  };

  const columns = [
    {
      title: T('Partner'),
      key: 'partner',
      render: (_: unknown, connection: Connection) => (
        <Partner>
          <PartnerLogo name={connection.partner_name} alias={connection.partner_alias} />
          <div>
            <Name>{connection.partner_name}</Name>
            <Sub>
              {connection.partner_alias} ·{' '}
              {connection.partner_has_app ? T('app configured') : T('no app')}
              {(connection.partner_account_count || 0) > 1 &&
                ` · ${connection.partner_account_count} ${T('accounts')}`}
            </Sub>
          </div>
        </Partner>
      ),
    },
    {
      title: T('Contract'),
      key: 'contract',
      render: (_: unknown, connection: Connection) => (
        <Chip bidirectional={connection.contract_type === 'BIDIRECTIONAL'}>
          {directionLabel(connection.contract_type)}
        </Chip>
      ),
    },
    {
      title: T('Stations I share'),
      key: 'shared',
      align: 'right' as const,
      render: (_: unknown, connection: Connection) => (
        <Numeric>
          <strong>{connection.shared_station_count}</strong>
          <Sub>{sharingLabel(connection)}</Sub>
        </Numeric>
      ),
    },
    {
      title: T('Stations I receive'),
      key: 'received',
      align: 'right' as const,
      render: (_: unknown, connection: Connection) => (
        <Numeric>
          <strong>{connection.received_station_count}</strong>
          <Sub>{receivingLabel(connection)}</Sub>
        </Numeric>
      ),
    },
    {
      title: T('Status'),
      key: 'status',
      render: (_: unknown, connection: Connection) => {
        const style = STATUS_STYLE[connection.status];

        return (
          <StatusBadge tone={style}>
            {T(style.label)}
          </StatusBadge>
        );
      },
    },
    {
      title: T('Last session'),
      key: 'lastSession',
      render: (_: unknown, connection: Connection) =>
        connection.last_session_at?.slice(0, 16).replace('T', ' ') || '—',
    },
    {
      title: '',
      key: 'open',
      width: 40,
      render: () => (
        <Chevron>
          <RightOutlined />
        </Chevron>
      ),
    },
  ];

  if (!connections.length) {
    return (
      <Empty>
        <p>{T('You have no connected partners yet')}</p>
        <LinkButton onClick={onInvite}>{T('Invite partner')}</LinkButton>
        <LinkButton onClick={onEnterToken}>{T('I have an invite token')}</LinkButton>
      </Empty>
    );
  }

  return (
    <Wrapper>
      <Note>
        {T(
          'A contract is only bidirectional when both tenants have an app configured — without an app the partner can share stations but receives none. Turning receiving on or off does not change the contract: it only hides the partner stations in your app.',
        )}
      </Note>

      <FilterBar>
        <Input.Search
          allowClear
          style={{ maxWidth: 260 }}
          placeholder={TF('Search partner by name or alias')}
          value={filter.search}
          onChange={(event) => changeFilter({ search: event.target.value })}
        />

        <Select
          value={filter.status}
          style={{ minWidth: 170 }}
          onChange={(status: ConnectionFilter['status']) => changeFilter({ status })}
        >
          <Select.Option value="">{T('All statuses')}</Select.Option>
          <Select.Option value="ACTIVE">{T('Active')}</Select.Option>
          <Select.Option value="PENDING">{T('Pending invite')}</Select.Option>
          <Select.Option value="SUSPENDED">{T('Suspended')}</Select.Option>
          <Select.Option value="REVOKED">{T('Revoked')}</Select.Option>
        </Select>

        <Select
          value={filter.contractType}
          style={{ minWidth: 170 }}
          onChange={(contractType: ConnectionFilter['contractType']) =>
            changeFilter({ contractType })}
        >
          <Select.Option value="">{T('Any contract')}</Select.Option>
          {/*
            Os tres tipos, com o mesmo rotulo da coluna Contrato: quem filtra por "← Unilateral"
            precisa reconhecer na linha o que escolheu. Os dois unilaterais sao opostos e antes
            dividiam uma unica opcao.
          */}
          <Select.Option value="BIDIRECTIONAL">{directionLabel('BIDIRECTIONAL')}</Select.Option>
          <Select.Option value="RECEIVING">{directionLabel('RECEIVING')}</Select.Option>
          <Select.Option value="SHARING">{directionLabel('SHARING')}</Select.Option>
        </Select>

        {/* A pergunta que aparece na operação: de quem eu estou exibindo estação hoje. */}
        <ThemedCheckbox
          checked={filter.onlyReceiving}
          onChange={(event) => changeFilter({ onlyReceiving: event.target.checked })}
        >
          {T('Only the ones I receive from')}
        </ThemedCheckbox>

        <FilterSpacer />

        <FilterCount>
          {countLabel(filtered.length, connections.length, TF('connections'), TF('of'))}
        </FilterCount>

        {viewSwitch}
      </FilterBar>

      {mapView && mapView(filtered)}

      {!mapView && (
        <>
          <Table
            rowKey="partner_tenant_id"
            rowClassName="interop-row"
            locale={{
              // Nenhum resultado por filtro é diferente de nenhuma conexão: quem filtrou
              // precisa saber que existe contrato fora do filtro.
              emptyText: T('No connection matches these filters.'),
            }}
            pagination={false}
            columns={columns}
            dataSource={visible}
            onRow={(connection: Connection) => ({
              onClick: () => onExpandPartner(connection.partner_tenant_id),
            })}
          />

          {/* Paginador do dashboard, 1-based como ele espera. */}
          <Pagination
            currentPage={currentPage}
            pageSize={PAGE_SIZE}
            totalCount={filtered.length}
            onPageChange={(next: number) => setPage(next)}
          />
        </>
      )}

      <Drawer
        width={480}
        visible={!!opened}
        onClose={() => onExpandPartner(null)}
        title={
          opened && (
            <DrawerTitle>
              <PartnerLogo name={opened.partner_name} alias={opened.partner_alias} />
              <div>
                <Name>{opened.partner_name}</Name>
                <Sub>{opened.partner_alias}</Sub>
              </div>
            </DrawerTitle>
          )
        }
      >
        {opened && (
          <ConnectionDrawerDetails
            tenantId={tenantId}
            connection={opened}
            onChanged={onChanged}
            onClose={() => onExpandPartner(null)}
          />
        )}
      </Drawer>
    </Wrapper>
  );
};
